import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import Editor, { type Monaco } from '@monaco-editor/react';
import { useTranslation } from 'react-i18next';
import ModalShell from 'components/common/ModalShell';
import { buttonClasses } from 'components/common/tailwindClassnames';
import { MONACO_OPTIONS } from 'components/routes/schema-builder/editorConfig';

type EditorExpandModalProps = {
  isOpen: boolean;
  title: string;
  language: string;
  value: string;
  onChange: (value: string) => void;
  readOnly: boolean;
  onClose: () => void;
  onBeforeMount?: (monaco: Monaco) => void;
};

export function EditorExpandModal({
  isOpen,
  title,
  language,
  value,
  onChange,
  readOnly,
  onClose,
  onBeforeMount,
}: EditorExpandModalProps) {
  const { t } = useTranslation();
  // Edit a local draft; only commit to the parent on Save. Closing (X / overlay / Cancel)
  // discards the draft. Reseed the draft only when the modal opens — never on later
  // `value` changes, so a background re-serialization can't clobber in-progress edits.
  const [draft, setDraft] = useState(value);
  useEffect(() => {
    if (isOpen) setDraft(value);
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!isOpen) return null;

  const handleSave = () => {
    onChange(draft);
    onClose();
  };

  return createPortal(
    <ModalShell
      title={title}
      onClose={onClose}
      maxWidthClass="max-w-5xl"
      footer={
        !readOnly ? (
          <>
            <button
              type="button"
              className={buttonClasses.primarySm}
              onClick={handleSave}
            >
              {t('Save')}
            </button>
            <button
              type="button"
              className={buttonClasses.cancelSm}
              onClick={onClose}
            >
              {t('Cancel')}
            </button>
          </>
        ) : undefined
      }
    >
      <div className="h-[60vh] overflow-hidden rounded border border-ui-field-outline">
        <Editor
          height="100%"
          language={language}
          value={draft}
          theme="vs"
          options={MONACO_OPTIONS(readOnly)}
          beforeMount={onBeforeMount}
          onChange={(v) => setDraft(v ?? '')}
        />
      </div>
    </ModalShell>,
    document.body,
  );
}
